import { format } from "date-fns";
import { useState } from "react";
import { Bar, BarChart, CartesianGrid, Tooltip, XAxis, YAxis } from "recharts";

import { Card } from "@/components/card";
import { LoadingScreen } from "@/components/loading-screen";
import { Badge } from "@/components/ui/badge";
import { ChartContainer } from "@/components/ui/chart";

import { useGetDailyAggregatedSession } from "../api/get-daily-aggregated-session";
import { Timeframe } from "../types/timeframe";
import { generateTimeRange } from "../utils/generate-time-range";
import {
  groupAverageSessionDuration,
  groupByDurationRange,
} from "../utils/group-chart-data";

import { TimeframeSelector } from "./timeframe-selector";

type AverageSessionDurationProps = {
  organisationId?: string;
};

export function AverageSessionDuration({
  organisationId,
}: AverageSessionDurationProps) {
  const [timeframe, setTimeframe] = useState<Timeframe>("w");

  const { from, to } = generateTimeRange(timeframe);
  const {
    data: dailyAggregatedSession,
    isSuccess,
    isLoading,
  } = useGetDailyAggregatedSession({
    from: from.toISOString(),
    to: to.toISOString(),
    organisationId,
  });

  const groupedData = isSuccess
    ? groupAverageSessionDuration(dailyAggregatedSession, timeframe)
    : [];
  const groupedByDurationRange = groupByDurationRange(groupedData);

  return (
    <Card>
      <div className="flex items-center justify-between">
        <Card.Header
          title={
            <span>
              Average Session Duration
              <Badge variant="outline" className="ml-1 font-normal">
                {format(from, "MMM dd")} - {format(to, "MMM dd")}
              </Badge>
            </span>
          }
        />
        <TimeframeSelector
          value={timeframe}
          onChange={(value) => setTimeframe(value as Timeframe)}
        />
      </div>

      <Card.Content className="relative">
        {isLoading && (
          <LoadingScreen className="absolute inset-0 z-10 h-full w-full" />
        )}
        <ChartContainer config={{}} className="h-[240px] w-full">
          <BarChart data={groupedByDurationRange}>
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis dataKey="range" />
            <YAxis type="number" allowDecimals={false} />
            <Tooltip
              cursor={false}
              contentStyle={{
                backgroundColor: "#161B26",
                color: "hsl(var(--card-foreground))",
                borderRadius: "var(--radius)",
                padding: "12px",
                border: "none",
              }}
              labelFormatter={(value) => {
                return `Lasting ${value} Minutes`;
              }}
              formatter={(value) => {
                return `${value} Sessions`;
              }}
              labelClassName="text-white"
            />
            <Bar
              dataKey="count"
              fill="#B692F6"
              activeBar={{ fill: "hsl(var(--primary))" }}
              radius={[8, 8, 0, 0]}
              barSize={36}
            />
          </BarChart>
        </ChartContainer>
      </Card.Content>
    </Card>
  );
}
